import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Transfer, Button, App, Divider, Checkbox, Table, Skeleton, Row, Col } from "antd";
import API from "API";
import { useParams } from "react-router-dom";
import { searchItem } from "general_function";
import { NotFound } from "AppRoute";

const TRANSFER_WIDTH = 800;
const TRANSFER_HEIGHT = 290;

const studentColumns = [
    { title: "", key: "check_box", dataIndex: "check_box" },
    { title: "Mã SV", key: "student_number", dataIndex: "student_number" },
    { title: "Username", key: "username", dataIndex: "username" },
    { title: "Họ và tên", key: "full_name", dataIndex: "full_name" },
    { title: "Email", key: "email", dataIndex: "email" },
]

export default function MajorAssignment() {
    const { id } = useParams();
    const { message } = App.useApp();

    const [students, setStudents] = useState([]);
    const [courses, setCourses] = useState([]);
    const [major, setMajor] = useState();
    const [selectedStudentIds, setSelectedStudentIds] = useState([]);
    const [selectedCourseIds, setSelectedCourseIds] = useState([]);
    const [mandatoryCourses, setMandatoryCourses] = useState([]);

    const [submitingStudent, setSubmittingStudent] = useState(false);
    const [submitingCourse, setSubmittingCourse] = useState(false);
    const [getting, setGetting] = useState(true);

    useEffect(() => {
        if (!major) return;
        document.title = major.name;
    },[major])

    const fetchData = useCallback(async () => {
        try {
            const [studentsRes, coursesRes, majorStudentsRes, assignedCoursesRes, major] = await Promise.all([
                API.get("/students/", { params: { page_size: 1000 } }),
                API.get("/courses/", { params: { page_size: 1000 } }),
                API.post("/enroll-student-major/", { major_id: id }),
                API.post("/enroll-course-major/", { major_id: id }),
                API.get(`/majors/${id}`),
            ]);

            setStudents((studentsRes.data.results || []).map((s) => ({
                ...s,
                key: String(s.id), full_name: s.last_name.trim() + " " + s.first_name.trim()
            })));
            setCourses((coursesRes.data.results || []).map((c) => ({ ...c, key: String(c.id) })));
            setSelectedStudentIds((majorStudentsRes.data || []).map((s) => String(s.id)));
            setSelectedCourseIds((assignedCoursesRes.data || []).map((c) => String(c.id)));
            setMandatoryCourses(
                (assignedCoursesRes.data || []).filter((c) => c.man).map((c) => String(c.id))
            );
            setMajor(major.data);
        } catch (err) {
            console.error("Lỗi khi tải dữ liệu ngành:", err);
            message.error("Không thể tải dữ liệu ngành học.");
        } finally {
            setGetting(false);
        }
    }, [id, message]);

    useEffect(() => {
        if (id) fetchData();
    }, [id, fetchData]);

    const handleSubmitStudents = async () => {
        try {
            setSubmittingStudent(true);
            await API.post("/enroll-student-major/", {
                major_id: parseInt(id),
                students: selectedStudentIds.map((sid) => parseInt(sid)),
            });
            message.success("Cập nhật sinh viên thành công!");
            fetchData();
        } catch (err) {
            console.error("Lỗi khi cập nhật sinh viên:", err);
            message.error("Không thể cập nhật sinh viên.");
        } finally {
            setSubmittingStudent(false);
        }
    };

    const handleSubmitCourses = async () => {
        try {
            setSubmittingCourse(true);
            await API.post("/enroll-course-major/", {
                major_id: parseInt(id),
                courses: selectedCourseIds.map((cid) => ({
                    id: parseInt(cid),
                    man: mandatoryCourses.includes(cid),
                })),
            });
            message.success("Cập nhật môn học thành công!");
            fetchData();
        } catch (err) {
            console.error("Lỗi khi cập nhật môn học:", err);
            message.error("Không thể cập nhật môn học.");
        } finally {
            setSubmittingCourse(false);
        }
    };

    const creditInfo = useMemo(() => {
        const result = {
            mandatoryCredit: 0,
            optionCredit: 0,
            totalCredit: 0,
        };
        for (const courseId of selectedCourseIds) {
            const course = courses.find(item => item.id === +courseId);
            if (mandatoryCourses.includes(courseId)) {
                result.mandatoryCredit += course.credit;
            }
            result.totalCredit += course.credit;
        }
        result.optionCredit = result.totalCredit - result.mandatoryCredit;
        return result;
    },[courses, mandatoryCourses, selectedCourseIds])

    return getting ? <Skeleton active /> : !major ? <NotFound />
    : (
        <div style={{ padding: 24 }}>
            <h4>Ngành: {major.name}</h4>

            <Divider orientation="left">Sinh viên</Divider>
            <Transfer
                style={{ height: 400 }}
                dataSource={students}
                titles={["Chưa đăng ký", "Đã đăng ký"]}
                targetKeys={selectedStudentIds}
                onChange={setSelectedStudentIds}
                showSearch
                filterOption={(inputValue, option) => {
                    return searchItem(inputValue, option, ["student_number", "first_name", "last_name", "email", "username"])
                }}
                listStyle={{ width: TRANSFER_WIDTH, height: TRANSFER_HEIGHT }}
                children={({ filteredItems, onItemSelect, onItemSelectAll, selectedKeys }) => {
                    const rowSelection = {
                        onChange(selectedRowKeys) {
                            onItemSelectAll(selectedRowKeys, 'replace');
                        },
                        selectedRowKeys: selectedKeys,
                        selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
                    };
                    return <div style={{ height: 300, overflow: "auto" }}>
                        <div style={{ width: 800 }}>
                        <Table
                            columns={studentColumns}
                            dataSource={filteredItems}
                            rowSelection={rowSelection}
                            style={{ width: 800, minWidth: "100%" }}
                            pagination={false}
                            onRow={({ key, disabled: itemDisabled }) => ({
                                onClick: () => {
                                    onItemSelect(key, !selectedKeys.includes(key));
                                },
                            })}
                        />
                        </div>
                    </div>
                }}
            />

            <div style={{ textAlign: "center", marginTop: 16 }}>
                <Button type="primary" onClick={handleSubmitStudents} disabled={submitingStudent} loading={submitingStudent}>
                    Lưu thay đổi sinh viên
                </Button>
            </div>

            <Divider orientation="left" style={{ marginTop: 48 }}>Môn học</Divider>
            <Row>
                <Col lg={12} md={24} sm={24}>
                    <b>Số tín chỉ bắt buộc: </b> <span style={{
                        color: creditInfo.mandatoryCredit > major.credit ? "red" : null
                    }}>
                        {creditInfo.mandatoryCredit}/{major.credit}
                    </span>
                </Col>
                <Col lg={12} md={24} sm={24}>
                    <b>Số tín chỉ tự chọn: </b> <span style={{
                        color: creditInfo.mandatoryCredit > major.credit ? "red" : null
                    }}>
                        {major.credit - creditInfo.mandatoryCredit}/{major.credit} ({creditInfo.optionCredit})
                    </span>
                </Col>
            </Row>
            <Transfer
                dataSource={courses.map((c) => ({ ...c, title: c.name }))}
                titles={["Chưa thêm", "Đã thêm"]}
                targetKeys={selectedCourseIds}
                onChange={(newKeys) => {
                    setSelectedCourseIds(newKeys);
                    setMandatoryCourses((prev) => prev.filter((id) => newKeys.includes(id)));
                }}
                filterOption={(inputValue, option) => {
                    return searchItem(inputValue, option, ["name"])
                }}
                render={(item) => {
                    const isMandatory = mandatoryCourses.includes(item.key);

                    return <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>{item.title}</span>
                        <Checkbox
                            checked={isMandatory}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                setMandatoryCourses((prev) =>
                                    checked ? [...prev, item.key] : prev.filter((id) => id !== item.key)
                                );
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            Bắt buộc
                        </Checkbox>
                    </div>
                }}
                showSearch
                listStyle={{ width: TRANSFER_WIDTH, height: TRANSFER_HEIGHT }}
            />

            <div style={{ textAlign: "center", marginTop: 16 }}>
                <Button type="primary" onClick={handleSubmitCourses} disabled={submitingCourse} loading={submitingCourse}>
                    Lưu thay đổi môn học
                </Button>
            </div>
        </div>
    );
}
